import React, { useEffect, useState, useMemo } from "react";
import { theme } from "antd";
import SummaryCard from "../Analytics/SummaryCard";
import SummaryGraphs from "../Analytics/SummaryGraphs";
import dayjs from "dayjs";
import localizedFormat from "dayjs/plugin/localizedFormat";
import SummaryGrid from "../Analytics/SummaryGrid";
import EmptyCard from "../EmptyCard";
import { convertiSecondiAMinuti } from "../../services/utils/analyticsUtils";
import LoadingCard from "../Components/Cards/LoadingCard";

dayjs.extend(localizedFormat);

function GatewaysOverviewCard({ loading, aggregateList = [], calculationFunction, onDataCalculated }) {
    const { token } = theme.useToken();
    const [userData, setUserData] = useState({});
    const [sessionData, setSessionData] = useState({});
    const [newUserData, setNewUserData] = useState({});
    const [avgTimeData, setAvgTimeData] = useState({});
    const [avgEngTimeData, setAvgEngTimeData] = useState({});
    const [avgSessionPerUserData, setAvgSessionPerUserData] = useState({});
    const [selectedDataIndex, setSelectedDataIndex] = useState(0);
    const [chartData, setChartData] = useState(null);

    const handleDataIndexChange = (index) => {
        setSelectedDataIndex(index);
    };

    // Testi informativi per i tooltip delle card
    const tooltipTextMap = {
        "USERS": "Number of distinct users who visited",
        "SESSIONS": "Total user sessions",
        "NEW USERS": "Users who visited for the first time",
        "AVG TIME": "Average time spent per session",
        "AVG ENG TIME": "Average time spent per user",
        "AVG S/U": "Average number of sessions per user"
    };

    // Memorizziamo i dati processati per evitare calcoli ripetuti quando cambiano solo token o selectedDataIndex
    const processedData = useMemo(() => {
        if (!Array.isArray(aggregateList) || aggregateList.length === 0) {
            return null;
        }

        let calculatedMetrics = null;
        let gatewayStats = {};
        const ALL_GATEWAYS_KEY = "_total_aggregate_";

        // Inizializziamo l'oggetto per le statistiche
        gatewayStats[ALL_GATEWAYS_KEY] = {
            users: new Set(),
            sessions: 0,
            newUsers: new Set(),
            totalEngagementTime: 0,
            viewingTotalTime: 0,
            dailyVisualizationTimes: {},
            dailyUniqueUsers: {},
            dailyNewUsers: {},
        };

        // Se è disponibile una funzione di calcolo esterna, usiamola
        if (calculationFunction) {
            try {
                calculatedMetrics = calculationFunction(aggregateList);
            } catch (error) {
                console.error("Error using calculation function:", error);
                // Proceed with internal calculation in case of error
            }
        }

        // Calcolo interno delle metriche (se la funzione di calcolo non è disponibile o ha fallito)
        // Processiamo ogni sessione una sola volta
        aggregateList.forEach((session) => {
            const { session_start_date, page_analitycs_list, device_id, is_first_session } = session;

            // Prendiamo i dati per l'aggregazione totale
            const statsData = gatewayStats[ALL_GATEWAYS_KEY];

            // Convertiamo la data della sessione
            const sessionDate = dayjs(session_start_date, "YYYY/MM/DD HH:mm:ss");
            if (!sessionDate.isValid()) return;
            const formattedDate = sessionDate.format("YYYY-MM-DD");

            // Tracciamo gli utenti unici 
            if (!statsData.dailyUniqueUsers[formattedDate]) {
                statsData.dailyUniqueUsers[formattedDate] = new Set();
            }
            statsData.dailyUniqueUsers[formattedDate].add(device_id);
            statsData.users.add(device_id);

            // Tracciamo i nuovi utenti
            if (!statsData.dailyNewUsers[formattedDate]) {
                statsData.dailyNewUsers[formattedDate] = new Set();
            }
            if (is_first_session) {
                statsData.dailyNewUsers[formattedDate].add(device_id);
                statsData.newUsers.add(device_id);
            }

            // Contiamo il numero di sessioni
            statsData.sessions++;

            // Sommiamo il tempo di visualizzazione totale
            if (page_analitycs_list && Array.isArray(page_analitycs_list)) {
                const sessionTotalTime = page_analitycs_list.reduce(
                    (sum, page) => sum + (page.visualizazion_time || 0),
                    0
                );

                statsData.totalEngagementTime += sessionTotalTime;
                statsData.viewingTotalTime += sessionTotalTime;

                if (!statsData.dailyVisualizationTimes[formattedDate]) {
                    statsData.dailyVisualizationTimes[formattedDate] = {
                        totalTime: 0,
                        sessionCount: 0,
                    };
                }

                statsData.dailyVisualizationTimes[formattedDate].totalTime += sessionTotalTime;
                statsData.dailyVisualizationTimes[formattedDate].sessionCount++;
            }
        });

        // Calcolo finale delle statistiche aggregate
        const totals = gatewayStats[ALL_GATEWAYS_KEY];

        // Calcoliamo le medie giornaliere
        const dailyAverages = Object.entries(totals.dailyVisualizationTimes)
            .sort(([a], [b]) => new Date(a) - new Date(b))
            .map(([date, { totalTime, sessionCount }]) => ({
                date,
                avgTime: totalTime / sessionCount,
                sessionCount,
                totalTime,
            }));

        const avgTime = dailyAverages.length > 0
            ? dailyAverages.reduce((sum, { avgTime }) => sum + avgTime, 0) / dailyAverages.length
            : 0;

        const totalUsers = totals.users.size;
        const totalSessions = totals.sessions;
        const totalNewUsers = totals.newUsers.size;
        const averageTimeSeconds = avgTime;
        const averageEngTimeSeconds = totals.totalEngagementTime / (totalUsers || 1);
        const averageSessionsPerUser = totalUsers > 0 ? totalSessions / totalUsers : 0;

        // Prepara i dati per i grafici
        const dailyUserCounts = Object.entries(totals.dailyUniqueUsers)
            .sort(([a], [b]) => new Date(a) - new Date(b))
            .map(([date, users]) => ({
                date,
                userCount: users.size,
            }));

        const dailyNewUserCounts = Object.entries(totals.dailyNewUsers)
            .sort(([a], [b]) => new Date(a) - new Date(b))
            .map(([date, userSet]) => ({
                date,
                newUserCount: userSet.size,
            }));

        // Calcoliamo i totali usando la funzione di calcolo se disponibile
        let finalTotals = {
            users: totalUsers,
            sessions: totalSessions,
            newUsers: totalNewUsers,
            avgTime: convertiSecondiAMinuti(averageTimeSeconds),
            avgEngTime: convertiSecondiAMinuti(averageEngTimeSeconds),
            avgSU: averageSessionsPerUser.toFixed(2)
        };

        if (calculatedMetrics) {
            // Sommiamo i valori di ogni gateway
            let totalCalcUsers = 0;
            let totalCalcSessions = 0;
            let totalCalcNewUsers = 0;
            let totalAvgTimeSeconds = 0;
            let totalAvgEngTimeSeconds = 0;
            let totalSessionCount = 0;

            calculatedMetrics.forEach(gateway => {
                // Sommiamo i valori per utenti, sessioni e nuovi utenti
                totalCalcUsers += gateway.Users;
                totalCalcSessions += gateway.Sessions;
                totalCalcNewUsers += gateway["New Users"];

                // Per i tempi medi, dobbiamo considerare il peso di ogni gateway
                // Prima convertiamo i valori di tempo da formato MM:SS a secondi
                let avgTimeInSecs = 0;
                let avgEngTimeInSecs = 0;

                if (typeof gateway["Avg Time"] === 'string') {
                    // Se è una stringa nel formato MM:SS, converte in secondi
                    const parts = gateway["Avg Time"].split(':');
                    avgTimeInSecs = parseInt(parts[0]) * 60 + parseInt(parts[1]);
                } else {
                    avgTimeInSecs = gateway["Avg Time"] || 0;
                }

                if (typeof gateway["Avg Eng Time"] === 'string') {
                    // Se è una stringa nel formato MM:SS, converte in secondi
                    const parts = gateway["Avg Eng Time"].split(':');
                    avgEngTimeInSecs = parseInt(parts[0]) * 60 + parseInt(parts[1]);
                } else {
                    avgEngTimeInSecs = gateway["Avg Eng Time"] || 0;
                }

                // Sommiamo i tempi totali (tempo medio × numero di sessioni)
                totalAvgTimeSeconds += avgTimeInSecs * gateway.Sessions;
                totalAvgEngTimeSeconds += avgEngTimeInSecs * gateway.Users;
                totalSessionCount += gateway.Sessions;
            });

            // Calcoliamo i valori finali
            // Per i tempi medi, dividiamo per il numero totale di sessioni/utenti
            const finalAvgTime = totalCalcSessions > 0 ? totalAvgTimeSeconds / totalCalcSessions : 0;
            const finalAvgEngTime = totalCalcUsers > 0 ? totalAvgEngTimeSeconds / totalCalcUsers : 0;
            const finalAvgSessionsPerUser = totalCalcUsers > 0 ? totalCalcSessions / totalCalcUsers : 0;

            finalTotals = {
                users: totalCalcUsers,
                sessions: totalCalcSessions,
                newUsers: totalCalcNewUsers,
                avgTime: convertiSecondiAMinuti(finalAvgTime),
                avgEngTime: convertiSecondiAMinuti(finalAvgEngTime),
                avgSU: finalAvgSessionsPerUser.toFixed(2)
            };
        }

        // Prepariamo i dati per le diverse metriche nei grafici
        const metricMap = [
            {
                label: "USERS",
                data: dailyUserCounts.map(({ userCount }) => userCount),
                dates: dailyUserCounts.map(({ date }) => dayjs(date).format("DD/MMM/YYYY"))
            },
            {
                label: "SESSIONS",
                data: dailyAverages.map(({ sessionCount }) => sessionCount),
                dates: dailyAverages.map(({ date }) => dayjs(date).format("DD/MMM/YYYY"))
            },
            {
                label: "NEW USERS",
                data: dailyNewUserCounts.map(({ newUserCount }) => newUserCount),
                dates: dailyNewUserCounts.map(({ date }) => dayjs(date).format("DD/MMM/YYYY"))
            },
            {
                label: "AVG TIME",
                data: dailyAverages.map(({ avgTime }) =>
                    convertiSecondiAMinuti(avgTime)
                ),
                dates: dailyAverages.map(({ date }) => dayjs(date).format("DD/MMM/YYYY"))
            },
            {
                label: "AVG ENG TIME",
                data: dailyAverages.map(({ totalTime, date }) => {
                    const dailyUsers = totals.dailyUniqueUsers[date]?.size || 1;
                    return convertiSecondiAMinuti(totalTime / dailyUsers);
                }),
                dates: dailyAverages.map(({ date }) => dayjs(date).format("DD/MMM/YYYY"))
            },
            {
                label: "AVG S/U",
                data: dailyAverages.map(({ sessionCount, date }) => {
                    const dailyUsers = totals.dailyUniqueUsers[date]?.size || 1;
                    return (sessionCount / dailyUsers).toFixed(2);
                }),
                dates: dailyAverages.map(({ date }) => dayjs(date).format("DD/MMM/YYYY"))
            },
        ];

        return {
            finalTotals,
            metricMap
        };
    }, [aggregateList, calculationFunction]);

    // Aggiorna gli stati in base ai dati processati (solo quando cambiano davvero)
    useEffect(() => {
        if (!processedData) {
            // Mantieni uno stato vuoto se non ci sono dati
            setUserData({ total: 0 });
            setSessionData({ total: 0 });
            setNewUserData({ total: 0 });
            setAvgTimeData({ total: "0:00" });
            setAvgEngTimeData({ total: "0:00" });
            setAvgSessionPerUserData({ total: "0.00" });
            setChartData(null);
            return;
        }

        const { finalTotals, metricMap } = processedData;

        // Aggiorniamo lo stato con i valori calcolati
        setUserData({ total: finalTotals.users });
        setSessionData({ total: finalTotals.sessions });
        setNewUserData({ total: finalTotals.newUsers });
        setAvgTimeData({ total: finalTotals.avgTime });
        setAvgEngTimeData({ total: finalTotals.avgEngTime });
        setAvgSessionPerUserData({ total: finalTotals.avgSU });

        // Inviamo i dati calcolati al componente genitore
        if (typeof onDataCalculated === 'function') {
            onDataCalculated(finalTotals);
        }

        // Aggiorniamo il grafico in base alla selezione
        const selectedMetric = metricMap[selectedDataIndex];

        if (selectedMetric && selectedMetric.data && selectedMetric.data.length > 0) {
            setChartData({
                labels: selectedMetric.dates,
                datasets: [
                    {
                        label: selectedMetric.label,
                        data: selectedMetric.data,
                        borderColor: token.colorPrimary,
                        backgroundColor: "transparent",
                    },
                ],
            });
        } else {
            setChartData(null);
        }
    }, [processedData, onDataCalculated, selectedDataIndex, token.colorPrimary]);

    // Verifica se ci sono dati da visualizzare
    const hasData = aggregateList && aggregateList.length > 0;

    // Array delle etichette per i card
    const cardLabels = [
        "USERS",
        "SESSIONS",
        "NEW USERS",
        "AVG TIME",
        "AVG ENG TIME",
        "AVG S/U",
    ];

    return (
        <>
            <SummaryGrid>
                {[
                    userData,
                    sessionData,
                    newUserData,
                    avgTimeData,
                    avgEngTimeData,
                    avgSessionPerUserData,
                ].map((item, idx) => (
                    <SummaryCard
                        key={`${item?.total || 0}_${idx}`}
                        loading={loading}
                        value={item?.total || 0}
                        active={idx === selectedDataIndex}
                        label={cardLabels[idx]}
                        onClick={() => handleDataIndexChange(idx)}
                        toolTipText={tooltipTextMap[cardLabels[idx]]}
                        index={idx}
                        totalitems={cardLabels.length}
                    />
                ))}
            </SummaryGrid>
            {loading ? (
                <LoadingCard />
            ) : hasData ? (
                <SummaryGraphs
                    data={chartData}
                    loading={loading}
                    activeTab={cardLabels[selectedDataIndex]}
                    toolTipText={tooltipTextMap[cardLabels[selectedDataIndex]]}
                />
            ) : (
                <EmptyCard />
            )}
        </>
    );
}

export default GatewaysOverviewCard;
